import { Component, inject, Inject, OnInit, signal } from '@angular/core';
import { MatCardActions, MatCardModule } from "@angular/material/card";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { FeedstockService } from '../../../../services/feedstockservice';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { StreamsList } from '../../../../interfaces/streamsmodel';
import { PMInputData, RecurringLoadData } from '../../../../interfaces/streamrecurringLoad';
import { IdName, PriceMatrixList, PriceMatrixSalesRep } from '../../../../interfaces/pricematrixmodel';
import { SchedulingSitesResponse } from '../../../../interfaces/siteType';
import { LookUpService } from '../../../../services/lookupservice';
import { forkJoin, Subject, takeUntil } from 'rxjs';
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatInputModule } from '@angular/material/input';
import { AlertDialogComponent } from '../../../shared/components/alert-dialog/alert-dialog.component';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { ConfirmationDialogComponent, ConfirmationDialogData } from '../../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { AddeditPricematrixSalesrepComponent } from './addedit-pricematrix-salesrep/addedit-pricematrix-salesrep.component';
import { MatMenuModule } from '@angular/material/menu';
import { formatDateOnly, handleDateKeydown, numericInputLimit } from '../../../shared/commonutils/helper';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { MatSelectItems } from '../../../../interfaces/lookup';
import { SelectSearchComponent } from '../../../shared/components/select-search/select-search.component';
import { RolePermissionsService } from '../../../../services/role-permissions.service';
import { LoadScheduleControlFlags, ScreenName } from '../../../../interfaces/securityPermissions';
import { RoleService } from '../../../../services/role.service';

@Component({
  selector: 'app-addedit-pricematrix',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatButtonModule,
    MatDatepickerModule,
    MatCheckboxModule,
    MatDialogModule,
    MatIconModule,
    MatMenuModule,
    MatTableModule,
    DragDropModule,
    SelectSearchComponent
  ],
  templateUrl: './addedit-pricematrix.component.html',
  styleUrl: './addedit-pricematrix.component.scss'
})
export class AddeditPricematrixComponent implements OnInit {
  priceMatrixForm!: FormGroup;
  permissions = inject(RolePermissionsService);
  // State signals
  attemptSave = signal(false);
  isSaving = signal(false);
  dateError = signal<boolean>(false);
  isEditMode = signal<boolean>(false);
  streamID = signal<number | null>(null);
  genId = signal<number | null>(null);
  displayValue = signal<string>('');

  // placeholders for select options - fill from API
  tipFeeUOM = signal<IdName[]>([]);
  rebateUOM = signal<IdName[]>([]);
  packageTypes = signal<IdName[]>([]);
  haulers = signal<IdName[]>([]);
  filteredHaulers = signal<IdName[]>([]);
  salesReps = signal<IdName[]>([]);
  disposalSites = signal<SchedulingSitesResponse[]>([]);
  filtereddisposalSites = signal<SchedulingSitesResponse[]>([]);
  task: string = "";
  displayedPMSalesRepColumns: string[] = ['actions', 'salesRepName', 'effectiveStartDate',
    'effectiveEndDate', 'commEligible', 'percentage'];
  addtPmSalesRep = new MatTableDataSource<PriceMatrixSalesRep>([]);
  selectHaulerItems: MatSelectItems[] = [];
  private destroy$ = new Subject<void>();
  showSalesRepAddButton = signal(false);
  showSalesRepGridEditButton = signal(false);
  showSalesRepGridDeleteButton = signal(false);
  showBinRentalFee = signal(false);
  controlPermissions: LoadScheduleControlFlags = new LoadScheduleControlFlags();
  canSave: boolean = false;
  constructor(private fsService: FeedstockService,
    private lookup: LookUpService,
    public dialogRef: MatDialogRef<AddeditPricematrixComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PMInputData,
    private pmForm: FormBuilder, private dialog: MatDialog,
    private rolesService: RoleService) {
    console.log("PM Dialog data:", data);
    this.streamID.set(data.streamId);
    this.genId.set(data.genId);
    this.task = data.task;
    this.canSave = data.canSave;
    //new data to load
    this.showSalesRepAddButton.set(data.showSalesRepAddButton);
    this.showSalesRepGridEditButton.set(data.showSalesRepGridEditButton);
    this.showSalesRepGridDeleteButton.set(data.showSalesRepGridDeleteButton);
    this.showBinRentalFee.set(data.showBinRentalFee);
    if (data.pmInputData) {
      this.isEditMode.set(true);
    }
  }

  ngOnInit(): void {
    this._initializeForm();
    this.loadDropdownData();
  }

  private _initializeForm(): void {
    this.priceMatrixForm = this.pmForm.group({
      id: [0],
      containerType: [null],
      containerSize: [null],
      serviceFrequence: [null],
      serviceFrequence_UOM: [null],
      disposalSiteNames: [null],
      rentalRate: [null],
      haulRevenue: [null],
      tipFee: [null, [Validators.required]], // Mandatory field 1
      tipFee_UOM: [null, [Validators.required]], // Mandatory field 2
      rebateValue: [null],
      rebate_UOM: [null],
      rebate_UOMName: [null],
      eIS: [null, Validators.max(100)],
      fuelSurcharge: [null, Validators.max(100)],
      tipFee_UOMName: [null],
      extraPickup: [null],
      monthlyCharges: [null],
      allSites: [false],
      effective_StartDate: [new Date(), [Validators.required]], // Mandatory field 3
      effective_EndDate: [null],
      packageType: [null],
      packageTypeName: [null],
      createdDate: [null],
      createdBy: [null],
      lastModifiedDate: [null],
      lastModifiedBy: [null],
      disposalSitesIds: [null, [Validators.required]],
      disposalSitesNames: [null],
      streamID: [this.streamID()],
      genId: [this.genId()],
      demurrage: [null],
      emergency: [null],
      noShow: [null],
      partialLoadToYard: [null],
      selfHaul: [false],
      commissionEligible: [false],
      // salesRep: [null],
      // salesRepName: [null],
      haulFeePassThrough: [false],
      minimalLoadFee: [null],
      invoiceNA: [false],
      fuelSurchargePassThrough: [false],
      hid: [null],
      incurEstimatedProcessingCosts: [false],
      isReviewed: [false],
      reviewedBy: [localStorage.getItem("loggedUserName") ?? ""],
      reviewedDate: [null],
      environmentalFee_LTL: [null],
      environmentalFee_FTL: [null],
      unloadFee: [null],
      binRentalFee: [null]
    });
  }

  loadDropdownData() {
    let roles = this.rolesService.getRolesAsString();
    const tipfeeuoms$ = this.lookup.getMaterialUnitOfMeasureLookup(0);
    const rebateuoms$ = this.lookup.getMaterialUnitOfMeasureLookup(0);
    const packagetypes$ = this.lookup.getPackageTypes();
    const salesreps$ = this.lookup.getSalesRep();
    const haulers$ = this.lookup.getORHaulers();
    const pmSalesReps$ = this.fsService.getPMSalesRep(this.data.pmInputData?.id ?? 0);
    const disposalsites$ = this.fsService.getSchedulingSites(5, 5); // 5 = Disposal Site Type
    const perms$ = this.permissions.getRolePermissionsNew(roles, ScreenName.CustomerInfo, 0)

    forkJoin([
      perms$,
      tipfeeuoms$,
      rebateuoms$,
      packagetypes$,
      salesreps$,
      haulers$,
      disposalsites$,
      pmSalesReps$
    ])
    .pipe(
      takeUntil(this.destroy$)
    )
    .subscribe({
      next: ([        
        perms,
        tipfeeuoms,
        rebateuoms,
        packagetypes,
        salesreps,
        haulers,
        disposalsites,
        pmSalesReps
      ]) => {
        this.controlPermissions = perms;
        this.tipFeeUOM.set(tipfeeuoms);
        this.rebateUOM.set(rebateuoms);
        this.packageTypes.set(packagetypes);
        this.salesReps.set(salesreps);
        this.haulers.set(haulers);
        this.filteredHaulers.set(haulers);
        this.disposalSites.set(disposalsites);
        this.filtereddisposalSites.set(disposalsites);

        this.selectHaulerItems = haulers.map(item => ({
          id: item.id,
          name: item.name!
        }));

        console.log('PM Dropdown data loaded successfully sites.', disposalsites);

        if (this.isEditMode()) {
          this.populateFormData(this.data.pmInputData);
          this.addtPmSalesRep.data = pmSalesReps ?? [];
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  roundOff(event: Event, maxValue: number, maxDecimals: number, type: number, controlName: string): void {
    const input = event.target as HTMLInputElement;
    let val = input.value;

    val = numericInputLimit(event, maxValue, maxDecimals);

    this.displayValue.set(val);
    input.value = val;

    // 6. Parsing for the backing variables
    const isValueEmpty = val === '' || val === '.';
    if (isValueEmpty) {
      this.priceMatrixForm.get(controlName)?.setValue(null, { emitEvent: false });
    } else if (val.endsWith('.')) {
      this.priceMatrixForm.get(controlName)?.setErrors({ 'incompleteDecimal': true });
    } else {
      const parsedValue = parseFloat(val);
      this.priceMatrixForm.get(controlName)?.setValue(parsedValue, { emitEvent: false });
    }
    requestAnimationFrame(() => {
      if (document.activeElement === input) {
          input.value = val;
      }
  });
  }

  save(): void {
    this.attemptSave.set(true);

    if (!this.validate()) {
      this.priceMatrixForm.markAllAsTouched();
      return;
    }
    const formData = this.priceMatrixForm.getRawValue();
    let startDatate: Date | null = formData.effective_StartDate;
    let endDate: Date | null = formData.effective_EndDate;
    if (startDatate && endDate) {
      const start = new Date(startDatate);
      start.setHours(0, 0, 0, 0);

      const end = new Date(endDate);
      end.setHours(0, 0, 0, 0);

      if (start.getTime() > end.getTime()) {
        this.dialog.open(AlertDialogComponent, {
          data: {
            title: 'Alert',
            message: 'End date should be after Start date.'
          },
          disableClose: true,
          panelClass: 'delete-confirmation-dialog-nowrap'
        });
        return;
      }
    }

    this.isSaving.set(true);
    const payload = this.buildPayload();
    console.log("Pm saving", payload);

    this.fsService.savePM(payload).subscribe({
      next: (ok) => {
        this.isSaving.set(false);
        this.dialogRef.close(true);
      }
    });
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  validate(): boolean {
    let isBaseFormValid = this.priceMatrixForm.valid;
    let isValidDates = this._checkDateValidity();
    return isBaseFormValid && isValidDates;
  }

  onHaulerReceived(event: number | null) {
    //this.haulerId = "MH-".concat(event?.toString() ?? '');
  }

  private _checkDateValidity(): boolean {
    this.dateError.set(false);
    const startDate = this.priceMatrixForm.get('effective_StartDate')?.value as Date | null;
    const endDate = this.priceMatrixForm.get('effective_EndDate')?.value as Date | null;

    if (startDate && endDate) {
      if (startDate.getTime() > endDate.getTime()) {
        this.dialog.open(AlertDialogComponent, {
          data: {
            title: 'Alert',
            message: 'End date should be after Start date.'
          }
        });
        this.dateError.set(true);
        return false;
      }
    }
    return true;
  }

  populateFormData(data: PriceMatrixList | null): void {
    console.log("loading edit data PM");
    if (data) {
      const rawIds = data.disposalSite ? data.disposalSite.split(',') : [];
      const availableSids = this.disposalSites().map(site => site.sid);
      const disposalSiteIdsArray = rawIds.filter(id => availableSids.includes(id));

      const formData = {
        // Map fields that are named differently in the PriceMatrixList (API format)
        rentalRate: data.rental_Rate, // List/API: rental_Rate -> Form: rentalRate
        haulRevenue: data.haulRate, // List/API: haulRate -> Form: haulRevenue
        eIS: data.eis, // List/API: eis -> Form: eIS
        tipFee_UOMName: data.tipFee_UOMV, // List/API: tipFee_UOMV -> Form: tipFee_UOMName
        extraPickup: data.extra_Pickup, // List/API: extra_Pickup -> Form: extraPickup
        monthlyCharges: data.monthly_Charges, // List/API: monthly_Charges -> Form: monthlyCharges
        partialLoadToYard: data.financeFee, // List/API: financeFee -> Form: partialLoadToYard
        minimalLoadFee: data.minimalCharges, // List/API: minimalCharges -> Form: minimalLoadFee

        // Map fields that have the same name in the List and Form
        id: data.id,
        containerType: data.containerType,
        containerSize: data.containerSize,
        serviceFrequence: data.serviceFrequence,
        serviceFrequence_UOM: data.ServiceFreq_UOM,
        disposalSitesIds: disposalSiteIdsArray,
        disposalSiteNames: data.disposalSiteNames,
        tipFee: data.tipFee,
        tipFee_UOM: data.tipFee_UOM,
        rebateValue: data.rebateValue,
        rebate_UOM: data.rebate_UOM,
        rebate_UOMName: data.rebate_UOMName,
        fuelSurcharge: data.fuelSurcharge,
        allSites: data.allSites ?? false,
        effective_StartDate: data.effective_StartDate ? new Date(data.effective_StartDate) : null,
        effective_EndDate: data.effective_EndDate ? new Date(data.effective_EndDate) : null,
        packageType: data.packageType,
        packageTypeName: data.packageTypeName,

        streamID: data.streamID,
        genId: data.genId,
        demurrage: data.demurrage,
        emergency: data.emergency,
        noShow: data.noShow,
        selfHaul: data.selfHaul === null ? false : data.selfHaul === 1 ? true : false,
        commissionEligible: data.commissionEligible ?? false,
        //salesRep: data.salesRep,
        //salesRepName: data.salesRepName,
        haulFeePassThrough: data.haulFeePassThrough ?? false,
        invoiceNA: data.invoiceNA ?? false,
        fuelSurchargePassThrough: data.fuelSurchargePassThrough ?? false,
        hid: data.hid,
        incurEstimatedProcessingCosts: data.incurEstimatedProcessingCosts ?? false,
        isReviewed: data.isReviewed ?? false,
        reviewedBy: data.reviewedBy,
        reviewedDate: data.reviewedDate ? new Date(data.reviewedDate) : null,
        environmentalFee_LTL: data.environmentalFee_LTL ?? null,
        environmentalFee_FTL: data.environmentalFee_FTL ?? null,
        unloadFee: data.unloadingFee ?? null,
        binRentalFee: data.binRentalFee ?? null,
        pricematrixSalesReps: data.pricematrixSalesReps ?? []
      };

      this.addtPmSalesRep.data = data.pricematrixSalesReps ?? [];

      this.priceMatrixForm.patchValue(formData);
      // Update helper signal
      const isReviewed = data.isReviewed ?? false;
      if (this.isEditMode() && isReviewed) {
        const controlsToDisable = [
          'disposalSitesIds', 'tipFee', 'tipFee_UOM', 'rentalRate', 'haulRevenue',
          'monthlyCharges', 'extraPickup', 'rebate_UOM', 'rebateValue', 'eIS',
          'fuelSurcharge', 'minimalLoadFee', 'emergency', 'noShow',
          'partialLoadToYard', 'selfHaul', 'packageType', 'hid',
          'commissionEligible', 'invoiceNA', 'haulFeePassThrough',
          'fuelSurchargePassThrough', 'incurEstimatedProcessingCosts',
          'environmentalFee_LTL', 'environmentalFee_FTL', 'unloadFee', 'binRentalFee'
        ];
        if(this.showBinRentalFee()){
          controlsToDisable.push('binRentalFee')
        }

        if(!this.controlPermissions.allowIsReviewdToggle){
          controlsToDisable.push("isReviewed");
        }
        controlsToDisable.forEach(controlName => {
          this.priceMatrixForm.get(controlName)?.disable();
        });
      }
    }
  }

  buildPayload(): PriceMatrixList {
    const formData = this.priceMatrixForm.getRawValue();
    const disposalSiteString = formData.disposalSitesIds?.length
      ? formData.disposalSitesIds.join(',')
      : null;
    const addtSalesRep: PriceMatrixSalesRep[] = this.addtPmSalesRep.data.length > 0 ? this.addtPmSalesRep.data.map(rep => ({
      id: rep.id,
      priceMatrixId: rep.priceMatrixId,
      salesRepId: rep.salesRepId,
      salesRepName: rep.salesRepName,
      effectiveStartDate: rep.effectiveStartDate ? formatDateOnly(rep.effectiveStartDate) : null,
      effectiveEndDate: rep.effectiveEndDate === null ? null : formatDateOnly(rep.effectiveEndDate),
      commEligible: rep.commEligible,
      percentage: rep.percentage
    })) : [];
    return {
      id: formData.id ?? 0,
      containerType: formData.containerType,
      containerSize: formData.containerSize,
      serviceFrequence: formData.serviceFrequence,
      ServiceFreq_UOM: formData.serviceFrequence_UOM,
      disposalSiteNames: formData.disposalSiteNames,
      rental_Rate: formData.rentalRate,
      eis: formData.eIS,
      tipFee_UOMV: formData.tipFee_UOMName,
      extra_Pickup: formData.extraPickup,
      monthly_Charges: formData.monthlyCharges,
      financeFee: formData.partialLoadToYard,
      minimalCharges: formData.minimalLoadFee,

      tipFee: formData.tipFee,
      tipFee_UOM: formData.tipFee_UOM,
      rebateValue: formData.rebateValue,
      rebate_UOM: formData.rebate_UOM,
      rebate_UOMName: formData.rebate_UOMName,
      fuelSurcharge: formData.fuelSurcharge,
      allSites: formData.allSites,

      effective_StartDate: formData.effective_StartDate ? formatDateOnly(formData.effective_StartDate) : null,
      effective_EndDate: formData.effective_EndDate ? formatDateOnly(formData.effective_EndDate) : null,
      packageType: formData.packageType,
      packageTypeName: formData.packageTypeName,
      disposalSite: disposalSiteString,
      streamID: this.streamID(),
      genId: this.genId(),
      demurrage: formData.demurrage,
      emergency: formData.emergency,
      noShow: formData.noShow,
      selfHaul: formData.selfHaul === true ? 1 : 0,
      commissionEligible: formData.commissionEligible,
      salesRep: null,//formData.salesRep,
      salesRepName: '',//formData.salesRepName,
      haulFeePassThrough: formData.haulFeePassThrough,
      invoiceNA: formData.invoiceNA,
      fuelSurchargePassThrough: formData.fuelSurchargePassThrough,
      hid: formData.hid,
      incurEstimatedProcessingCosts: formData.incurEstimatedProcessingCosts,
      isReviewed: formData.isReviewed,

      extraPickup: formData.extraPickup,
      hauler: formData.haulerId,
      haul_Rate_Perload: formData.haulRevenue,

      reviewedBy: localStorage.getItem("loggedUserName") ?? "",
      reviewedDate: `${new Date().toISOString()}`,
      environmentalFee_LTL: formData.environmentalFee_LTL ?? null,
      environmentalFee_FTL: formData.environmentalFee_FTL ?? null,
      unloadingFee: formData.unloadFee ?? null,
      binRentalFee: formData.binRentalFee ?? null,

      pricematrixSalesReps: addtSalesRep
    } as PriceMatrixList;
  }

  addorEditSalesRep(row: PriceMatrixSalesRep | null, isEdit: boolean): void {
    {
      const formData = this.priceMatrixForm.getRawValue();
      const dialogRef = this.dialog.open(AddeditPricematrixSalesrepComponent, {
        disableClose: true,
        width: '650px',
        maxHeight: '90vh',
        data: row ? { ...row, task: 'Edit', priceMatrixId: formData.id || 0 } : { task: 'Add', priceMatrixId: formData.id || 0 }
      });

      dialogRef.afterClosed().subscribe((result: PriceMatrixSalesRep) => {
        if (result) {
          const currentData = this.addtPmSalesRep.data;
          if (!result.effectiveStartDate) {
            this.dialog.open(AlertDialogComponent, {
              data: {
                title: 'Alert',
                message: 'Start Date is required.'
              }
            });
            return;
          }
          const newStart = new Date(result.effectiveStartDate).getTime();
          const hasConflict = currentData.some(existing => {
            if (row && existing.id === row.id) return false;

            if (existing.salesRepId !== result.salesRepId) return false;

            const existingStart = existing.effectiveStartDate ? new Date(existing.effectiveStartDate).getTime() : 0;
            const existingEnd = existing.effectiveEndDate === null ? Infinity : new Date(existing.effectiveEndDate).getTime();

            return newStart >= existingStart && newStart <= existingEnd;
          });

          if (hasConflict) {
            this.dialog.open(AlertDialogComponent, {
              data: {
                title: 'Alert',
                message: `Start Date must not fall within existing records (Effective Start and End Dates) of ${result.salesRepName}`
              }
            });
            return;
          }
          if (row) {
            const index = currentData.findIndex(item => item.id === row.id);
            if (index > -1) {
              currentData[index] = result;
            }
          } else {
            currentData.push(result);
          }
          this.addtPmSalesRep.data = [...currentData];
        }
      });
    }
  }

  deleteSalesRep(index: number, salesRep: PriceMatrixSalesRep): void {
    if (index > 0) {
      this.confirmDeleteSalesRep(index, salesRep);
    }
    else {
      this.deletePMSalesRep(index);
    }
  }

  filterHaulers(event: Event): void {
    const input = event.target as HTMLInputElement;
    const searchTerm = input.value.toLowerCase();
    if (!searchTerm) {
      this.filteredHaulers.set(this.haulers());
    } else {
      const filteredhauls = this.haulers().filter(city => city.name.toLowerCase().includes(searchTerm));
      this.filteredHaulers.set(filteredhauls);
    }
  }

  onKeydown(event: KeyboardEvent) {
    if (event.ctrlKey && event.metaKey) { return; }
    if (!/[0-9\/]/.test(event.key) && event.key.length === 1) {
      event.preventDefault();
    }
  }
  dateformat(event: Event) {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    if (!value) return;
    const parts = value.split('/');
    if (parts.length !== 3) return;

    let [month, day, year] = parts.map(part => part.padStart(2, '0'));
    if (month.length === 1) month = '0' + month;
    if (day.length === 1) day = '0' + day;
    if (year.length === 2) year = '20' + year;
    input.value = `${month}/${day}/${year}`;
  }

  confirmDeleteSalesRep(index: number, salesRep: PriceMatrixSalesRep): void {
    const dialogData: ConfirmationDialogData = {
      title: 'Confirm Deletion',
      message: `Are you sure you want to delete Sales rep ${salesRep.salesRepName}?`,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: 'warn'
    };
    this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      panelClass: 'delete-confirmation-dialog',
      data: dialogData,
      disableClose: true
    }).afterClosed().subscribe(result => {
      if (result) {
        this.deletePMSalesRep(index);
      }
    });
  }

  deletePMSalesRep(index: number): void {
    const salesRepToDelete = this.addtPmSalesRep.data.find(i => i.id === index);
    if (salesRepToDelete && salesRepToDelete.id && salesRepToDelete.id > 0) {
      this.fsService.deletePMSalesrep(salesRepToDelete.id).subscribe({
        next: () => {
          this.addtPmSalesRep.data = this.addtPmSalesRep.data.filter(item => item.id !== index);
        }
      });
    } else {
      const currentData = this.addtPmSalesRep.data;
      currentData.splice(index, 1);
      this.addtPmSalesRep.data = [...currentData];
    }
  }

  filterDisposalSites(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    const selectedIds = this.priceMatrixForm.get('disposalSitesIds')?.value || [];
    if (!searchTerm) {
      this.filtereddisposalSites.set([...this.disposalSites()]);
    } else {
      const filtered = this.disposalSites().filter(
        g => g.siteName.toLowerCase().includes(searchTerm) || selectedIds.includes(g.sid)
      );
      this.filtereddisposalSites.set(filtered);
    }
  }
}
